import { client as cashierClient } from "@/api/cashier/client.gen";
import { connectionStatus } from "@/api/connectionStatus/connectionStatus";
import { setFlowCorrelation } from "@/observability/datadog";
import { logger } from "@/observability/logger";
import { getBackendUrl } from "@/utils/preview";

// Track request start times for performance monitoring
const requestStartTimes = new Map<string, number>();

// Cleanup stale entries to prevent memory leaks
const REQUEST_TIMEOUT_MS = 60000;
const CLEANUP_INTERVAL_MS = 30000;
const FAILED_TO_FETCH = "Failed to fetch";

const cleanupStaleRequests = () => {
  const now = Date.now();
  const staleThreshold = now - REQUEST_TIMEOUT_MS;

  for (const [requestId, startTime] of requestStartTimes.entries()) {
    if (startTime < staleThreshold) {
      requestStartTimes.delete(requestId);
    }
  }
};

// Start periodic cleanup
let cleanupIntervalId: ReturnType<typeof setInterval> | null = null;

const startCleanupInterval = () => {
  if (!cleanupIntervalId) {
    cleanupIntervalId = setInterval(cleanupStaleRequests, CLEANUP_INTERVAL_MS);
  }
};

// Guard to prevent multiple setup calls
let clientsInitialized = false;

// Mutable token reference read by interceptors on every request,
// so a refreshed JWT is picked up without re-registering interceptors.
let currentAccessToken: string | null = null;

export const updateAccessToken = (token: string) => {
  currentAccessToken = token;
};

// Drops the in-memory bearer so no request can attach a stale JWT after logout.
export const clearAccessToken = () => {
  currentAccessToken = null;
};

// Invoked on the first 401 to hand control back to the auth layer (registered by
// MfeWrapperApp). The auth layer forces a fresh login so the rejected token is
// REPLACED — a plain redirect/reload cannot fix this because Auth0 re-reads the
// same (expired/revoked/tampered) token from its localStorage cache on the next
// load, producing an infinite re-auth loop. If no handler is registered we fall
// back to reloading the app origin.
let onUnauthorized: (() => void) | null = null;

export const setUnauthorizedHandler = (handler: () => void) => {
  onUnauthorized = handler;
};

// Guards against a 401 storm: a batch of in-flight requests can each resolve
// with 401, but we must react only once — otherwise we retrigger the redirect
// repeatedly and spin in a loop.
let handlingUnauthorized = false;

const handleUnauthorized = () => {
  if (handlingUnauthorized) return;
  handlingUnauthorized = true;
  // Never let a request attach the rejected token again.
  clearAccessToken();
  if (onUnauthorized) {
    onUnauthorized();
  } else {
    // Fallback when no handler is registered: reload the app origin so the
    // normal auth bootstrap re-runs. Using origin (not "/") keeps host+port.
    window.location.assign(window.location.origin);
  }
};

let currentCorrelationId: string = crypto.randomUUID();
if (
  import.meta.env.VITE_DATADOG_APPLICATION_ID &&
  import.meta.env.VITE_DATADOG_CLIENT_TOKEN
) {
  // Expose the initial flow id to RUM as well; setGlobalContextProperty is
  // safe to call before datadogRum.init (the SDK buffers pre-init calls).
  setFlowCorrelation(currentCorrelationId);
}

export const startFlowCorrelationId = (): string => {
  currentCorrelationId = crypto.randomUUID();
  // Keep RUM events joinable with backend logs for the new flow.
  setFlowCorrelation(currentCorrelationId);
  return currentCorrelationId;
};

export const getCorrelationId = (): string => currentCorrelationId;

// Update this array to add more api clients
const clients = [cashierClient];

export type ClientOptions = {
  accessToken: string;
};

export const setupClients = (options: ClientOptions) => {
  if (options.accessToken) {
    currentAccessToken = options.accessToken;
  }

  // Only initialize clients once to avoid duplicate interceptors
  if (clientsInitialized) return;

  // Start periodic cleanup of stale request timings
  startCleanupInterval();

  for (const client of clients) {
    setupClient(client);
  }
  clientsInitialized = true;
};

const setupClient = (client: (typeof clients)[number]) => {
  client.setConfig({
    baseUrl: getBackendUrl(),
  });

  client.interceptors.request.use(async (request) => {
    // Track request start time for performance monitoring.
    // This is a client-only timing key, not a transport header — the gateway
    // owns the per-request X-Amzn-Requestid.
    const requestId = crypto.randomUUID();
    requestStartTimes.set(requestId, Date.now());
    // Store request ID for later retrieval in response interceptor
    Object.assign(request, { __requestId: requestId });

    // One correlation id per flow, set when the flow starts (see
    // startFlowCorrelationId / useFlowCorrelationId), reused on every request.
    request.headers.set("X-Correlation-ID", currentCorrelationId);

    if (!currentAccessToken) {
      // A cashier request firing without a bearer token will 401 and block the
      // user — surface it to Datadog rather than a dev-only console.warn.
      logger.error("Missing access token on cashier API request", {
        event: "missing_access_token",
        correlation_id: currentCorrelationId,
      });
    } else {
      request.headers.set("Authorization", `Bearer ${currentAccessToken}`);
    }
    return request;
  });

  client.interceptors.response.use(async (response, request) => {
    connectionStatus.setConnected();

    const requestId = (request as { __requestId?: string }).__requestId;

    const endpoint =
      response.url || (request as { url?: string }).url || "unknown";
    const method = (request as { method?: string }).method || "unknown";

    // Identifiers for end-to-end correlation with the backend in Datadog:
    // - correlationId: workflow-scoped id we send as X-Correlation-ID (also
    //   tagged on backend APM spans and present in every backend log line).
    // - requestId: per-request id the backend echoes as X-Request-ID (readable
    //   here because the backend exposes it via CORS).
    const correlationId =
      response.headers.get("X-Correlation-ID") ?? currentCorrelationId;
    const backendRequestId = response.headers.get("X-Request-ID") ?? undefined;

    // Track API performance if we have timing data
    if (requestId && requestStartTimes.has(requestId)) {
      const startTime = requestStartTimes.get(requestId);
      if (startTime !== undefined) {
        const duration = Date.now() - startTime;

        // Log slow API calls (>2 seconds).
        if (duration > 2000) {
          logger.warn("Slow API call detected", {
            event: "slow_api_call",
            endpoint,
            method,
            duration,
            threshold: 2000,
            correlation_id: correlationId,
            request_id: backendRequestId,
          });
        }
      }
    }

    // Track API errors when response has error status
    if (response.status >= 400) {
      logger.error("API request failed", {
        event: "api_error",
        endpoint,
        method,
        statusCode: response.status,
        error: response.statusText,
        correlation_id: correlationId,
        request_id: backendRequestId,
      });
    }

    if (requestId && requestStartTimes.has(requestId)) {
      requestStartTimes.delete(requestId);
    }

    if (response.status === 401) {
      handleUnauthorized();
    }

    return response;
  });

  client.interceptors.error.use(async (error, response, request) => {
    if (
      !response &&
      (error instanceof TypeError ||
        (error as Error)?.message === FAILED_TO_FETCH)
    ) {
      connectionStatus.setDisconnected();
    }

    const requestId = (request as { __requestId?: string })?.__requestId;
    if (requestId) {
      requestStartTimes.delete(requestId);
    }

    return error;
  });
};
